/**
 * RuleSheet — a bottom sheet on phones, a right-hand side panel on wider screens.
 * Sits above sticky bars (z 1100, like AppModal); tap outside or Esc to close.
 * `footer` stays pinned at the bottom, within thumb reach.
 */
import { useEffect } from 'react';

export default function RuleSheet({ open, onClose, title, subtitle, children, footer, testId }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[1100] flex items-end sm:items-stretch sm:justify-end bg-[#111827]/45"
      onClick={onClose} role="dialog" aria-modal="true" data-testid={testId}>
      <div
        className="bg-white w-full sm:max-w-md max-h-[88vh] sm:max-h-none sm:h-full rounded-t-2xl sm:rounded-none flex flex-col shadow-xl"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="sm:hidden w-9 h-1 rounded-full bg-gray-300 mx-auto mt-2" aria-hidden="true" />
        <div className="flex items-start justify-between gap-3 px-5 pt-3 sm:pt-5 pb-2 flex-shrink-0">
          <div className="min-w-0">
            <h3 className="font-extrabold text-lg text-[#2B3A67] leading-tight">{title}</h3>
            {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600 text-2xl leading-none -mr-1"
            data-testid={testId ? `${testId}-close` : undefined}>×</button>
        </div>
        <div className="px-5 pb-4 overflow-y-auto flex-1">{children}</div>
        {footer && <div className="px-5 py-3 border-t border-gray-100 flex-shrink-0 bg-white">{footer}</div>}
      </div>
    </div>
  );
}
